import React from "react";

import { SIM_BLOCK_HEIGHTS } from "../../../sim/blockHeights";
import { SimFrame } from "../../../sim/SimFrame";

/**
 * `framed` — the ergonomic framing prop every program/neobank sim block gains
 * via {@link framable}. When `true`, the block wraps its own rendered content
 * in {@link SimFrame} at ITS OWN max height (looked up once at export time from
 * {@link SIM_BLOCK_HEIGHTS}); when `false`/omitted it renders bare, exactly as
 * before (no extra DOM).
 */
export interface FramableProps {
    /**
     * Frame the block in a thin-bordered, fixed-height box at its own max
     * height, so the looping sim's build → fade → rebuild cycle changes content
     * INSIDE a stable box (no page-height oscillation, clean edge). The block
     * KNOWS its height — the consumer never passes a pixel value.
     *
     * For a custom height, drop `framed` and wrap the bare block in `SimFrame`
     * with an explicit `height` yourself (the manual escape hatch).
     */
    framed?: boolean;
    /** Extra classes on the frame element (only applied when `framed`). */
    frameClassName?: string;
}

/**
 * `framable(Component, heightKey)` — bind a sim block to its own max frame
 * height (from {@link SIM_BLOCK_HEIGHTS}) ONCE, colocated with the block's
 * export, and give it a `framed` capability.
 *
 * The returned component:
 *   - renders `Component` BARE when `framed` is false/omitted — identical DOM
 *     to before (the frame prop is stripped, the rest thread through);
 *   - wraps it in {@link SimFrame} at `SIM_BLOCK_HEIGHTS[heightKey]` when
 *     `framed` is true.
 *
 * The height is bound here, not passed by the caller — the sim owns its
 * height. `SimFrame` stays exported as the low-level mechanism / custom-height
 * escape hatch.
 *
 * ```ts
 * export const ProgramConfigurationSim = framable(
 *   ProgramConfigurationSimView,
 *   "ProgramConfigurationSim"
 * );
 * // consumer:
 * <ProgramConfigurationSim framed />
 * ```
 */
export function framable<P extends object>(
    Component: React.ComponentType<P>,
    heightKey: keyof typeof SIM_BLOCK_HEIGHTS
): React.FunctionComponent<P & FramableProps> {
    function Framable({
        framed,
        frameClassName,
        ...rest
    }: P & FramableProps): React.ReactElement {
        const element = <Component {...(rest as P)} />;
        if (!framed) {
            return element;
        }
        return (
            <SimFrame
                height={SIM_BLOCK_HEIGHTS[heightKey]}
                className={frameClassName}
            >
                {element}
            </SimFrame>
        );
    }
    Framable.displayName = `Framable(${
        Component.displayName ?? Component.name ?? heightKey
    })`;
    return Framable;
}
